import {
  Flex,
  Progress,
  Radio,
  RadioChangeEvent,
  Spin,
  Table,
  Typography,
} from "antd";
import {
  StyleActiveUserWrapper,
  StyledAbsoluteShowMore,
  StyledActiveUsersWrapper,
  StyledCollape,
  StyledDownOutlined,
  StyledProgressWrapper,
  StyledShowMore,
} from "./style";
import { useEffect, useRef, useState } from "react";

import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../store/config";
import { useParams } from "react-router-dom";
import { ITraineeCount } from "../../interfaces/store/dashboard-interface";
import { formatNumber } from "../../utils/utils";
import {
  getTraineeCountsStart,
  resetDashboardActiveUsers,
} from "../../store/features/dashboard/active-users/active-users-slice";
import { NotFoundText } from "../../components/common/styles";

export const ActiveUsers = () => {
  const dispatch = useAppDispatch();
  const { id: trainerId } = useParams<{ id: string }>();
  const { t } = useTranslation();

  const [type, setType] = useState("training-programs");
  const [skip, setSkip] = useState(0);
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);
  const [isAtEnd, setIsAtEnd] = useState(false);
  const [trainees, setTrainees] = useState<ITraineeCount[]>([]);

  const { traineesCount, loading } = useAppSelector(
    (state) => state.dashboardActiveUsers
  );
  const { traineesCount: statsTraineesCount } = useAppSelector(
    (state) => state.dashboardStats
  );

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    dispatch(getTraineeCountsStart({ trainerId, type, skip, limit: 5 }));
  }, [trainerId, type, skip]);

  useEffect(() => {
    if (traineesCount) {
      setIsAtEnd(traineesCount.length < 5);
      setTrainees((prevTrainees) => [...prevTrainees, ...traineesCount]);
    }
  }, [traineesCount]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      const isBottom =
        container.scrollHeight - container.scrollTop === container.clientHeight;
      setIsScrolledToBottom(isBottom);
    }
  }, [trainees]);

  useEffect(() => {
    return () => {
      dispatch(resetDashboardActiveUsers());
    };
  }, []);

  const onChange = (e: RadioChangeEvent) => {
    setType(e.target.value);
    setSkip(0);
    setTrainees([]);
  };

  const handleScroll = () => {
    const container = scrollContainerRef.current;
    if (container) {
      const scrollThreshold = 2;
      const scrolledPosition = container.scrollHeight - container.scrollTop;
      const isBottom =
        scrolledPosition - container.clientHeight <= scrollThreshold;
      setIsScrolledToBottom(isBottom);
    }
  };
  const loadMore = () => {
    setSkip((prevSkip) => prevSkip + 5);
  };

  const totalTraineeCount = statsTraineesCount?.total || 0;

  return (
    <StyleActiveUserWrapper>
      <Flex justify="space-between">
        <Typography.Title level={5}>{t("Active Users")} </Typography.Title>

        <Radio.Group onChange={onChange} defaultValue="training-programs">
          <Radio.Button value="training-programs">{t("Programs")}</Radio.Button>
          <Radio.Button value="workouts">{t("Workouts")}</Radio.Button>
        </Radio.Group>
      </Flex>
      <Spin spinning={loading} style={{ height: "495px" }}>
        <StyledActiveUsersWrapper
          ref={scrollContainerRef}
          onScroll={handleScroll}
        >
          {trainees.length === 0 && !loading ? (
            <NotFoundText>{t("Empty")}</NotFoundText>
          ) : (
            trainees.map((item, index) => (
              <ProgressData
                key={index}
                data={item}
                totalTraineeCount={totalTraineeCount}
              />
            ))
          )}
        </StyledActiveUsersWrapper>
      </Spin>
      {isScrolledToBottom && !isAtEnd && (
        <StyledAbsoluteShowMore>
          <StyledShowMore isBig={true} onClick={loadMore}>
            <StyledDownOutlined
              isActive={false}
              className="absolute-show-more"
            />
          </StyledShowMore>
        </StyledAbsoluteShowMore>
      )}
    </StyleActiveUserWrapper>
  );
};

const ProgressData = (props: {
  data: ITraineeCount;
  totalTraineeCount: number;
}) => {
  const { data, totalTraineeCount } = props;
  const { t } = useTranslation();

  const modifiedData = [
    {
      key: "1",
      category: t("Active Women"),
      amount: data.female,
      percentage: formatNumber((data.female / data.total) * 100 || 0) + "%",
    },
    {
      key: "2",
      category: t("Active Men"),
      amount: data.male,
      percentage: formatNumber((data.male / data.total) * 100 || 0) + "%",
    },
    {
      key: "3",
      category: t("Average User Age"),
      amount: formatNumber(data.avgAge),
    },
  ];

  const columns = [
    {
      title: t("Category"),
      dataIndex: "category",
      key: "category",
    },
    {
      title: t("Amount"),
      dataIndex: "amount",
      key: "amount",
    },
    {
      title: t("Percentage"),
      dataIndex: "percentage",
      key: "percentage",
    },
  ];

  return (
    <StyledProgressWrapper>
      <Flex justify="space-between">
        <Typography.Text>{data.label}</Typography.Text>
        <Typography.Text>{data.total}</Typography.Text>
      </Flex>
      <Progress
        style={{ direction: "ltr" }}
        percent={(data.total / totalTraineeCount) * 100}
        showInfo={false}
        strokeColor={"#94A5F2"}
      />
      <StyledCollape
        expandIcon={(panelProps) => (
          <StyledShowMore>
            <StyledDownOutlined isActive={panelProps.isActive} />
          </StyledShowMore>
        )}
        items={[
          {
            key: "1",
            children: (
              <Table
                pagination={false}
                columns={columns}
                components={{
                  header: {
                    cell: (props: any) => (
                      <th
                        {...props}
                        style={{
                          backgroundColor: "transparent",
                          color: "#808080",
                          fontSize: "13px",
                          borderBottom: "5px solid white",
                        }}
                      />
                    ),
                  },
                  body: {
                    cell: (props: any) => (
                      <td
                        {...props}
                        style={{
                          color: "#808080",
                          fontSize: "13px",
                          lineHeight: "30px",
                        }}
                      />
                    ),
                  },
                }}
                dataSource={modifiedData}
              />
            ),
          },
        ]}
      />
    </StyledProgressWrapper>
  );
};
